import { useState, useEffect, useCallback } from 'react';
import {
  PromanDocument,
  PromanReport,
  PromanReportCollection,
  PromanProjectWithFolders,
  PromanFolder,
} from '../../../../../api/promanDtos';
import {
  apiGetPromanReportCollections,
  apiGetPromanReports,
  apiGetPromanDocuments,
  apiGetPromanMandateId,
  apiGetPromanProject,
} from '../../../../../api/proman';
import { mapDocumentsByReport } from '../utils/promanHelpers';

interface UsePromanDataReturn {
  // State
  promanProject: PromanProjectWithFolders | null;
  promanProjectFolders: PromanFolder[] | null;
  reportCollections: PromanReportCollection[];
  reportsByCollection: Record<number, PromanReport[]>;
  organizedDocuments: Record<string, PromanDocument[]>;
  mandateId: string | null;
  loading: boolean;
  loadingReports: Record<number, boolean>;
  loadingAllReports: boolean;
  reloadKey: number;

  // Methods
  loadReportsForCollection: (
    collectionId: number,
    force?: boolean
  ) => Promise<void>;
  loadAllCollectionsReports: () => Promise<void>;
  startLoadingAllReports: () => void;
  reloadAllData: () => Promise<void>;
}

export const usePromanData = (
  promanProjectId: number,
  activeCollections: (string | number)[]
): UsePromanDataReturn => {
  const [promanProject, setPromanProject] =
    useState<PromanProjectWithFolders | null>(null);
  const [promanProjectFolders, setPromanProjectFolders] = useState<
    PromanFolder[] | null
  >(null);
  const [reportCollections, setReportCollections] = useState<
    PromanReportCollection[]
  >([]);
  const [reportsByCollection, setReportsByCollection] = useState<
    Record<number, PromanReport[]>
  >({});
  const [organizedDocuments, setOrganizedDocuments] = useState<
    Record<string, PromanDocument[]>
  >({});
  const [mandateId, setMandateId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingReports, setLoadingReports] = useState<Record<number, boolean>>(
    {}
  );
  const [loadingAllReports, setLoadingAllReports] = useState<boolean>(false);
  const [reloadKey, setReloadKey] = useState<number>(0);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!promanProjectId) return;

      setLoading(true);

      try {
        const [
          projectResult,
          collectionsResult,
          documentsResult,
          mandateIdResult,
        ] = await Promise.all([
          apiGetPromanProject(promanProjectId),
          apiGetPromanReportCollections(promanProjectId),
          apiGetPromanDocuments(promanProjectId),
          apiGetPromanMandateId(),
        ]);

        if (projectResult.data) {
          setPromanProject(projectResult.data);
          setPromanProjectFolders(projectResult.data.folders);
        }

        if (collectionsResult.data) {
          setReportCollections(collectionsResult.data);

          if (documentsResult.data) {
            const documents = documentsResult.data || [];
            const organized = mapDocumentsByReport(
              documents,
              collectionsResult.data
            );
            setOrganizedDocuments(organized);
          }

          if (mandateIdResult.data) {
            setMandateId(mandateIdResult.data);
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [promanProjectId]);

  // Load reports for a specific collection
  const loadReportsForCollection = useCallback(
    async (collectionId: number, force: boolean = false) => {
      if (
        !force &&
        (reportsByCollection[collectionId] || loadingReports[collectionId])
      ) {
        return;
      }

      setLoadingReports((prev) => ({ ...prev, [collectionId]: true }));

      try {
        const result = await apiGetPromanReports(promanProjectId, collectionId);
        if (result.data) {
          setReportsByCollection((prev) => ({
            ...prev,
            [collectionId]: result.data,
          }));
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setLoadingReports((prev) => ({ ...prev, [collectionId]: false }));
      }
    },
    [promanProjectId, reportsByCollection, loadingReports]
  );

  // Load reports for all collections in parallel (for search functionality)
  const loadAllCollectionsReports = useCallback(async () => {
    if (!reportCollections.length) {
      setLoadingAllReports(false);
      return;
    }

    // Set loading to true immediately
    setLoadingAllReports(true);

    // Get all collection IDs that haven't been loaded yet
    const collectionIdsToLoad = reportCollections
      .map((col) => col.reportCollectionId)
      .filter(
        (id) => !reportsByCollection[id] && !loadingReports[id]
      );

    if (collectionIdsToLoad.length === 0) {
      // All collections already loaded, set loading to false immediately
      setLoadingAllReports(false);
      return;
    }

    // Mark all as loading
    setLoadingReports((prev) => {
      const updates: Record<number, boolean> = {};
      collectionIdsToLoad.forEach((id) => {
        updates[id] = true;
      });
      return { ...prev, ...updates };
    });

    // Load all in parallel for performance
    try {
      const results = await Promise.allSettled(
        collectionIdsToLoad.map((collectionId) =>
          apiGetPromanReports(promanProjectId, collectionId)
        )
      );

      // Process results
      const newReports: Record<number, PromanReport[]> = {};
      results.forEach((result, index) => {
        const collectionId = collectionIdsToLoad[index];
        if (result.status === 'fulfilled' && result.value.data) {
          newReports[collectionId] = result.value.data;
        }
      });

      // Update state with all loaded reports at once
      setReportsByCollection((prev) => ({
        ...prev,
        ...newReports,
      }));
    } catch (error) {
      console.error('Error fetching reports for all collections:', error);
    } finally {
      // Mark all as not loading
      setLoadingReports((prev) => {
        const updates: Record<number, boolean> = {};
        collectionIdsToLoad.forEach((id) => {
          updates[id] = false;
        });
        return { ...prev, ...updates };
      });
      setLoadingAllReports(false);
    }
  }, [promanProjectId, reportCollections, reportsByCollection, loadingReports]);

  // Start loading all reports immediately (synchronous)
  const startLoadingAllReports = useCallback(() => {
    setLoadingAllReports(true);
    // Trigger the actual loading
    loadAllCollectionsReports();
  }, [loadAllCollectionsReports]);

  // Reload all collections and documents
  const reloadAllData = useCallback(async () => {
    if (!promanProjectId) return;

    setLoading(true);

    try {
      const [projectResult, collectionsResult, documentsResult] =
        await Promise.all([
          apiGetPromanProject(promanProjectId),
          apiGetPromanReportCollections(promanProjectId),
          apiGetPromanDocuments(promanProjectId),
        ]);

      if (projectResult.data) {
        setPromanProject(projectResult.data);
      }

      if (collectionsResult.data) {
        setReportCollections(collectionsResult.data);
      }

      if (documentsResult.data) {
        const organized = mapDocumentsByReport(
          documentsResult.data || [],
          collectionsResult.data || []
        );
        setOrganizedDocuments(organized);
      }

      // Reload reports for active collections
      const active = [...activeCollections]
        .map((k) => (typeof k === 'string' ? parseInt(k) : k))
        .filter((n) => !isNaN(Number(n))) as number[];

      // Clear existing reports for those collections to force reload
      setReportsByCollection((prev) => {
        const copy = { ...prev };
        active.forEach((id) => delete copy[id]);
        return copy;
      });

      await Promise.all(active.map((id) => loadReportsForCollection(id, true)));

      // Force remount of expanded panels
      setReloadKey((k) => k + 1);
    } catch (error) {
      console.error('Error reloading PROMAN data:', error);
    } finally {
      setLoading(false);
    }
  }, [promanProjectId, activeCollections, loadReportsForCollection]);

  return {
    promanProject,
    promanProjectFolders,
    reportCollections,
    reportsByCollection,
    organizedDocuments,
    mandateId,
    loading,
    loadingReports,
    loadingAllReports,
    reloadKey,
    loadReportsForCollection,
    loadAllCollectionsReports,
    startLoadingAllReports,
    reloadAllData,
  };
};
